import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getServicesManager } from '../../../../services';
import { RootState } from '../../../../store';
import { 
  startRequest,
  requestError,
  stopRequest,
  SessionRequestType,
  updateSessionMetric,
  updateCustomMetric,
} from './data-slice';

export type CustomMetricFunctions = {
  requestCustomMetric: (sessionId: string) => Promise<void>
}

export const useCustomMetricFunctions = (): CustomMetricFunctions => {
  const dispatch = useDispatch();
  const sessionDetails = useSelector((state: RootState) => state.sessionDetails.sessionIdToDetails);
  
  const processCustomMetricRequest = useCallback(async (sessionId: string) => {
    try {
      // Call the service to request custom metrics
      return await getServicesManager().requestCustomMetric(sessionId);
    } catch (error) {
      console.error('Error requesting custom metrics:', error);
      return null;
    }
  }, []);

  const requestCustomMetric = useCallback(async (sessionId: string) => {
    const sessionData = sessionDetails[sessionId]?.data;
    if (!sessionData) return;
    
    // Check if the request is already loading
    if (sessionDetails[sessionId]?.requestStates?.[SessionRequestType.CUSTOM_SCORE]?.isLoading) return;
    
    dispatch(startRequest({ sessionId, requestType: SessionRequestType.CUSTOM_SCORE }));
    try {
      const result = await processCustomMetricRequest(sessionId);
      if (result) {
        dispatch(updateCustomMetric({ sessionId, metric: result.metric, detail: result.detail }));
      }
    } catch (error) {
      dispatch(requestError({ 
        sessionId, 
        requestType: SessionRequestType.CUSTOM_SCORE, 
        error: 'Unable to generate custom metrics' 
      }));
    } finally {
      dispatch(stopRequest({ sessionId, requestType: SessionRequestType.CUSTOM_SCORE }));
    }
  }, [sessionDetails, dispatch, processCustomMetricRequest]);

  return {
    requestCustomMetric,
  };
}; 